import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import { Block, ErrorOutline, Visibility } from '@mui/icons-material';
import { fetchPublicCard } from '../../api/cardApi';
import { trackCardView, trackCardShare } from '../../api/analyticsApi';
import DefaultTheme from './Themes/DefaultTheme';
import ModernTheme from './Themes/ModernTheme';
import MinimalistTheme from './Themes/MinimalistTheme';
import CorporateTheme from './Themes/CorporateTheme';
import StartupTheme from './Themes/StartupTheme';

const themeMap = {
    default: DefaultTheme,
    modern: ModernTheme,
    minimalist: MinimalistTheme,
    corporate: CorporateTheme,
    startup: StartupTheme,
};

const ErrorPage = ({ errorType, message }) => {
    const getErrorIcon = () => {
        switch (errorType) {
            case 'unavailable':
                return <Block sx={{ fontSize: 80, color: '#ff6b6b', mb: 2 }} />;
            case 'not-found':
                return <ErrorOutline sx={{ fontSize: 80, color: '#ffa726', mb: 2 }} />;
            default:
                return <ErrorOutline sx={{ fontSize: 80, color: '#666', mb: 2 }} />;
        }
    };

    const getErrorTitle = () => {
        switch (errorType) {
            case 'unavailable':
                return 'Portfolio Unavailable';
            case 'not-found':
                return 'Portfolio Not Found';
            default:
                return 'Error';
        }
    };

    const getErrorDescription = () => {
        switch (errorType) {
            case 'unavailable':
                return 'This portfolio is currently unavailable. Please contact the portfolio owner for more information.';
            case 'not-found':
                return 'The portfolio you are looking for could not be found. Please check the URL and try again.';
            default:
                return message || 'An unexpected error occurred.';
        }
    };

    return (
        <Box 
            sx={{ 
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Container maxWidth="sm">
                <Paper 
                    elevation={8}
                    sx={{ 
                        p: 6, 
                        textAlign: 'center', 
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    {getErrorIcon()}
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            fontWeight: 700, 
                            color: '#333', 
                            mb: 2 
                        }}
                    >
                        {getErrorTitle()}
                    </Typography>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: '#666', 
                            mb: 4, 
                            lineHeight: 1.6 
                        }}
                    >
                        {getErrorDescription()}
                    </Typography>
                    <Button 
                        variant="contained" 
                        size="large"
                        onClick={() => window.history.back()}
                        sx={{ 
                            px: 4, 
                            py: 1.5, 
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                            }
                        }}
                    >
                        Go Back
                    </Button>
                </Paper>
            </Container>
        </Box>
    );
};

const LoadingPage = () => (
    <Box 
        sx={{ 
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
        }}
    >
        <Container maxWidth="sm">
            <Paper 
                elevation={8}
                sx={{ 
                    p: 6, 
                    textAlign: 'center', 
                    borderRadius: 4,
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Visibility sx={{ fontSize: 80, color: '#667eea', mb: 2 }} />
                <Typography 
                    variant="h5" 
                    sx={{ 
                        fontWeight: 600, 
                        color: '#333', 
                        mb: 2 
                    }}
                >
                    Loading Portfolio...
                </Typography>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: '#666' 
                    }}
                >
                    Please wait while we prepare the portfolio for you.
                </Typography>
            </Paper>
        </Container>
    </Box>
);

const PublicCard = ({ card: initialCardData, isPreview }) => {
    console.log(`\n🚀 PUBLIC CARD COMPONENT LOADED! 🚀`);
    console.log(`📊 Component Props:`, { initialCardData: !!initialCardData, isPreview });
    
    const { slug } = useParams();
    const location = useLocation();
    const isEditMode = location.pathname.includes('/edit-card');
    
    console.log(`📂 URL Params:`, { slug });
    console.log(`📍 Location:`, window.location.href);
    
    const [card, setCard] = useState(initialCardData);
    const [loading, setLoading] = useState(!initialCardData);
    const [error, setError] = useState(null);
    
    // Track if analytics have been sent for this page visit
    const analyticsTrackedRef = useRef(false);
    const currentUrlRef = useRef(window.location.href);

    useEffect(() => {
        console.log(`\n⚡ useEffect TRIGGERED! ⚡`);
        console.log(`🔍 Conditions:`, { 
            isPreview, 
            hasInitialCardData: !!initialCardData,
            slug,
            shouldFetchCard: !initialCardData && slug
        });
        
        if (isPreview && initialCardData) {
            console.log(`🎭 PREVIEW MODE - Using initial card data`);
            setCard(initialCardData);
            setLoading(false);
            return;
        }

        if (!initialCardData && slug) {
            console.log(`🌐 NORMAL MODE - Fetching card data for slug: ${slug}`);
            setLoading(true);
            setError(null);
            
            fetchPublicCard(slug)
                .then(data => {
                    console.log(`\n🎯 PUBLIC CARD LOADED SUCCESSFULLY! 🎯`);
                    console.log(`📂 Card Data:`, data);
                    console.log(`🏷️  Card Slug: ${slug}`);
                    console.log(`📝 Card Name: ${data.name}`);
                    console.log(`👤 Card Owner: User#${data.user_id}`);
                    
                    setCard(data);
                    setError(null);
                    
                    // Check if this is a QR scan visit with multiple detection methods
                    const urlParams = new URLSearchParams(location.search);
                    const referer = document.referrer;
                    const userAgent = navigator.userAgent.toLowerCase();
                    const isMobile = /mobile|android|iphone|ipad|phone/.test(userAgent);
                    
                    const qrDetectionReasons = [];
                    let isQRScan = false;
                    
                    if (urlParams.get('qr') === 'true' || urlParams.get('qr') === '1') {
                        qrDetectionReasons.push('URL param qr=true');
                        isQRScan = true;
                    }
                    if (urlParams.get('source') === 'qr') {
                        qrDetectionReasons.push('URL param source=qr');
                        isQRScan = true;
                    }
                    if (urlParams.get('utm_source') === 'qr' || urlParams.get('utm_medium') === 'qr_code') {
                        qrDetectionReasons.push('UTM parameters indicate QR scan');
                        isQRScan = true;
                    }
                    if (!referer && isMobile) {
                        qrDetectionReasons.push('Direct mobile access (likely QR)');
                        isQRScan = true;
                    }
                    if (urlParams.get('utm_source') === 'qr') {
                        qrDetectionReasons.push('UTM source indicates QR');
                        isQRScan = true;
                    }
                    
                    console.log('📱 QR scan detection:', { 
                        urlParams: urlParams.toString(), 
                        referer, 
                        isMobile,
                        userAgent,
                        isQRScan,
                        reasons: qrDetectionReasons
                    });
                    
                    if (isQRScan) {
                        console.log('🔄 Detected QR scan, tracking share...');
                        const trackShare = async () => {
                            const success = await trackCardShare(slug, 'qr_scan');
                            if (!success) {
                                console.warn('⚠️ QR share tracking failed, retrying in 2 seconds...');
                                setTimeout(() => trackCardShare(slug, 'qr_scan'), 2000);
                            }
                        };
                        trackShare();
                    }
                })
                .catch(err => {
                    console.error("Failed to load card", err);
                    setCard(null);
                    
                    // Handle different error types based on status code
                    if (err.response) {
                        const { status, data } = err.response;
                        if (status === 403) {
                            // User disabled or card unavailable
                            setError({ type: 'unavailable', message: data.detail });
                        } else if (status === 404) {
                            // Card not found
                            setError({ type: 'not-found', message: data.detail });
                        } else {
                            setError({ type: 'error', message: 'Failed to load portfolio' });
                        }
                    } else {
                        setError({ type: 'error', message: 'Network error. Please try again.' });
                    }
                })
                .finally(() => setLoading(false));
        } else if (initialCardData) {
            console.log(`📋 USING EXISTING CARD DATA - No fetch needed`);
            setCard(initialCardData);
        }

        // Track portfolio view - This should run for ALL cases where we have a slug (not preview)
        if (slug && !isPreview) {
            // Create a unique key for this page visit
            const currentUrl = window.location.href;
            const sessionKey = `analytics_tracked_${slug}_${currentUrl}`;
            
            // Check if URL changed (for navigation between different cards)
            if (currentUrlRef.current !== currentUrl) {
                console.log(`🔄 URL changed from ${currentUrlRef.current} to ${currentUrl} - resetting analytics tracking`);
                analyticsTrackedRef.current = false;
                currentUrlRef.current = currentUrl;
                // Clear previous session tracking for different URLs
                sessionStorage.removeItem(`analytics_tracked_${slug}_${currentUrlRef.current}`);
            }
            
            // Check if analytics already tracked for this session
            const alreadyTracked = analyticsTrackedRef.current || sessionStorage.getItem(sessionKey);
            
            console.log(`\n📊 ANALYTICS TRACKING CHECK...`);
            console.log(`🎯 Slug: ${slug}`);
            console.log(`📍 Current URL: ${currentUrl}`);
            console.log(`🔍 Already tracked (ref): ${analyticsTrackedRef.current}`);
            console.log(`🔍 Already tracked (session): ${!!sessionStorage.getItem(sessionKey)}`);
            console.log(`❓ Should track: ${!alreadyTracked}`);
            
            if (!alreadyTracked) {
                console.log(`\n📊 INITIATING ANALYTICS TRACKING...`);
                console.log(`🎯 About to call trackCardView() for slug: ${slug}`);
                console.log(`📍 Current location: ${window.location.href}`);
                console.log(`🕐 Track time: ${new Date().toISOString()}`);
                console.log(`🔄 Initial card data exists: ${!!initialCardData}`);
                
                // Mark as tracked immediately to prevent duplicates
                analyticsTrackedRef.current = true;
                sessionStorage.setItem(sessionKey, Date.now().toString());
                
                // Track portfolio view with retry
                const trackView = async () => {
                                console.log(`🚀 Calling trackCardView(${slug})...`);
            const success = await trackCardView(slug);
                    if (!success) {
                        console.warn(`⚠️  TRACKING FAILED! Retrying in 2 seconds for slug: ${slug}`);
                        setTimeout(() => {
                                            console.log(`🔄 RETRY: Calling trackCardView(${slug}) again...`);
                trackCardView(slug);
                        }, 2000);
                    } else {
                        console.log(`✅ TRACKING SUCCESS! Portfolio view recorded for: ${slug}`);
                    }
                };
                
                trackView();
            } else {
                console.log(`⏭️  SKIPPING ANALYTICS - Already tracked for this session`);
            }
        }
    }, [slug, initialCardData, isPreview]);

    if (loading) {
        return <LoadingPage />;
    }
    
    if (error) {
        return <ErrorPage errorType={error.type} message={error.message} />;
    }
    
    if (!card) {
        return <ErrorPage errorType="not-found" />;
    }

    const ThemeComponent = themeMap[card.theme] || DefaultTheme;

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <ThemeComponent 
                card={card} 
                isPreview={isPreview} 
                isEditMode={isEditMode}
            />
        </Box>
    );
};

export default PublicCard; 